import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../../components/Modal';
import { proofreadText } from '../utils/proofreader';

/**
 * Modal som visar alla föreslagna ändringar fält-för-fält.
 *
 * Vid öppning byggs en snapshot av suggestions från `fields`. Snapshot:en
 * ändras inte när parent-state uppdateras under tiden — så Använd/Hoppa
 * över fungerar predikterbart även medan användaren klickar igenom.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Array<{id: string, label: string, value: string, onApply: (newValue: string) => void}>} fields
 */
const ProofreadModal = ({ isOpen, onClose, fields }) => {
  const [pending, setPending] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const initialized = useRef(false);

  useEffect(() => {
    if (isOpen && !initialized.current) {
      const initial = (fields || [])
        .filter(f => f.value && typeof f.value === 'string' && f.value.trim().length > 0)
        .map(f => {
          const { fixed, changes } = proofreadText(f.value, f.options || {});
          if (!changes.length || fixed === f.value) return null;
          // edited = nuvarande textarea-värde (kan ändras av användaren)
          return { id: f.id, label: f.label, value: f.value, fixed, edited: fixed, changes, onApply: f.onApply };
        })
        .filter(Boolean);
      setPending(initial);
      setTotalCount(initial.length);
      initialized.current = true;
    }
    if (!isOpen) {
      initialized.current = false;
    }
  }, [isOpen, fields]);

  const handleEdit = (id, newValue) => {
    setPending(prev => prev.map(s => s.id === id ? { ...s, edited: newValue } : s));
  };

  const handleApply = (suggestion) => {
    suggestion.onApply(suggestion.edited);
    setPending(prev => prev.filter(s => s.id !== suggestion.id));
  };

  const handleSkip = (suggestion) => {
    setPending(prev => prev.filter(s => s.id !== suggestion.id));
  };

  const handleApplyAll = () => {
    pending.forEach(s => s.onApply(s.edited));
    setPending([]);
  };

  const renderDiff = (suggestion) => (
    <div className="space-y-1.5 text-xs">
      <div className="flex gap-2">
        <span className="text-gray-400 shrink-0 w-12 pt-1">Före:</span>
        <span className="text-gray-700 whitespace-pre-wrap break-words pt-1">{suggestion.value}</span>
      </div>
      <div className="flex gap-2">
        <span className="text-leadme-pink shrink-0 w-12 font-medium pt-1">Efter:</span>
        <textarea
          value={suggestion.edited}
          onChange={(e) => handleEdit(suggestion.id, e.target.value)}
          rows={Math.max(1, Math.min(6, suggestion.edited.split('\n').length))}
          className="flex-1 px-2 py-1 text-xs text-gray-900 font-medium bg-leadme-pink/5 border border-leadme-pink/30 rounded focus:outline-none focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink resize-y"
        />
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Korrekturläs anteckningar</h2>
            <p className="text-sm text-gray-500 mt-1">
              Granskar formalia: stor bokstav, punkt, mellanslag. Stavning kontrolleras inte.
            </p>
          </div>
          {pending.length > 0 && (
            <button
              type="button"
              onClick={handleApplyAll}
              className="shrink-0 px-3 py-1.5 text-xs font-medium bg-leadme-pink hover:bg-leadme-pink-dark text-white rounded-md transition-colors"
            >
              Använd alla ({pending.length})
            </button>
          )}
        </div>

        {totalCount === 0 ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <svg className="mx-auto h-10 w-10 text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-sm font-medium text-green-800">Inga formaliafel hittades.</p>
            <p className="text-xs text-green-700 mt-1">Alla anteckningar ser bra ut.</p>
          </div>
        ) : pending.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-600">Klart — alla förslag har hanterats.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {pending.map(s => (
              <div key={s.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                      {s.label}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {s.changes.map((c, idx) => (
                        <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleSkip(s)}
                      className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 rounded"
                    >
                      Hoppa över
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApply(s)}
                      className="px-2 py-1 text-xs font-medium text-white bg-leadme-pink hover:bg-leadme-pink-dark rounded"
                    >
                      Använd
                    </button>
                  </div>
                </div>
                {renderDiff(s)}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Stäng
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ProofreadModal;
